/**
 * The edit history is a list of these plain, serialisable ops (see IMPLEMENTATION_PLAN.md: ops,
 * not state snapshots, are what persists and what a future activity database would replay). Each
 * op maps to one domain edit function; `fold` replays a prefix of the list to derive the current
 * EditState. Undo/redo is just moving a cursor over this list.
 */
import { mergeLengths, setPoolLength, setStroke, splitLength, type EditState } from '../domain/edit'

export type Op =
  | { readonly type: 'merge'; readonly ordinals: number[] }
  | { readonly type: 'split'; readonly ordinal: number; readonly parts: number }
  | { readonly type: 'setStroke'; readonly ordinals: number[]; readonly value: number }
  | { readonly type: 'setPool'; readonly cm: number }

export function applyOp(state: EditState, op: Op): EditState {
  switch (op.type) {
    case 'merge':
      return mergeLengths(state, op.ordinals)
    case 'split':
      return splitLength(state, op.ordinal, op.parts)
    case 'setStroke':
      return setStroke(state, op.ordinals, op.value)
    case 'setPool':
      return setPoolLength(state, op.cm)
  }
}

/** Replay ops in order over an initial state. Throws if any op's precondition fails. */
export function fold(initial: EditState, ops: readonly Op[]): EditState {
  return ops.reduce<EditState>(applyOp, initial)
}
